/**
 * Banka ücret / kesinti hareketlerini tanır.
 *
 * Havale-EFT işlemlerinde bankanın kestiği masraf, komisyon, BSMV gibi satırlar
 * açıklamada bir "karşı taraf adı" gibi görünüyor ("KESİNTİ VE EKLERİ BE…").
 * Sistem bunları yeni bir müşteri sanıp kimlik havuzuna yazıyor ve fatura
 * kesmeye çalışıyordu. Bu kontrol onları ayıklar.
 *
 * Aynı liste sunucu tarafında functions/identity/bankMovementIdentityResolver.js
 * içinde de bulunur; ikisi birlikte güncellenmelidir.
 */

/** Türkçe harf katlaması + küçük harf (ı/İ dahil). */
const fold = (value) =>
  String(value || '')
    .replace(/[ıİ]/g, 'i')
    .replace(/[şŞ]/g, 's')
    .replace(/[ğĞ]/g, 'g')
    .replace(/[üÜ]/g, 'u')
    .replace(/[öÖ]/g, 'o')
    .replace(/[çÇ]/g, 'c')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Ücret/kesinti göstergeleri. Yalnızca masrafa özgü kelimeler:
 * "havale" / "eft" tek başına normal bir müşteri transferinde de geçtiği için
 * bilerek listede yok.
 */
const BANK_FEE_TERMS = [
  'kesinti',
  'masraf',
  'komisyon',
  'ucret',
  'bsmv',
  'damga vergisi',
  'hesap isletim',
  'hesap bakim',
  'aidat',
  'blokaj',
  'faiz tahakkuk',
  'ekstre bedeli',
  'islem bedeli',
  'kart bedeli',
  'gecikme'
];

/**
 * Ekranda banka masrafı hareketinin karşı tarafı için yazılan ad.
 * Ham veride "KESİNTİ VE EKLERİ BE", "HESABINDAN TAHSİLİ" gibi açıklama
 * kırıntıları geliyor; bunlar kişi değil.
 */
export const BANKA_MASRAF_ETIKETI = 'İşlem Masrafı';

/** Bir metin banka ücreti/kesintisi mi anlatıyor. */
export const looksLikeBankFeeText = (value) => {
  const text = fold(value);
  if (!text) return false;
  return BANK_FEE_TERMS.some((term) => text.includes(term));
};

/**
 * Hareketin kendisi bir banka ücreti/kesintisi mi.
 * Karşı taraf adı ve açıklama birlikte değerlendirilir.
 */
export const isBankFeeMovement = (movement) => {
  if (!movement) return false;
  const candidates = [
    movement.counterpartyName,
    movement.senderName,
    movement.gonderenAdi,
    movement.description,
    movement.aciklama,
    movement.rawData?.senderName,
    movement.rawData?.gonderenAdi
  ];
  return candidates.some(looksLikeBankFeeText);
};

export default isBankFeeMovement;

/* ------------------------------------------------------------------ *
 * Masraf / komisyon sınıflandırması
 *
 * İki banka POS komisyonunu farklı raporluyor:
 *  - VakıfBank: brüt tutarı yatırır, komisyonu AYRI bir giden hareket yazar
 *      "... nolu pos ve batchinin komisyonu"            → -382,53
 *  - Garanti:  net tutarı yatırır, komisyonu açıklamaya gömer
 *      "PK3761132 BONUS 09/12 K: 220,66"                → +7.159,34
 * ------------------------------------------------------------------ */

/** Hareketin metin havuzu (ad + açıklama). */
const feeHaystack = (movement) =>
  fold(
    [
      movement?.counterpartyName,
      movement?.receiverName,
      movement?.senderName,
      movement?.description,
      movement?.aciklama
    ]
      .filter(Boolean)
      .join(' ')
  );

/** "220,66" / "1.234,56" → 1234.56 */
const parseTrAmount = (raw) => {
  const text = String(raw || '').trim().replace(/\./g, '').replace(',', '.');
  const n = Number(text);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Garanti POS günsonu açıklamasındaki komisyon: "... K: 220,66"
 * @returns {number} komisyon tutarı (yoksa 0)
 */
export const parseGarantiPosCommission = (movement) => {
  const text = String(movement?.description || movement?.aciklama || '');
  if (!/\bK\s*:/i.test(text)) return 0;
  const m = text.match(/\bK\s*:\s*([\d.]+,\d{2})/i);
  return m ? parseTrAmount(m[1]) : 0;
};

/** POS komisyonu mu — "komisyon" tek başına yetmez, "pos"/"batch" de gerekir. */
const looksLikePosCommission = (text) =>
  /\bpos\b|batchin|batch/.test(text) && /komisyon/.test(text);

/**
 * Bir hareketin masraf/komisyon katkısı.
 * @returns {{ kind: 'pos'|'transfer'|null, amount: number }}
 */
export const classifyBankFee = (movement) => {
  if (!movement) return { kind: null, amount: 0 };

  const text = feeHaystack(movement);
  const abs = Math.abs(Number(movement.amount) || 0);
  const isOut =
    String(movement.direction || '').toLowerCase() === 'out' || Number(movement.amount) < 0;

  // Garanti: gelen POS günsonunun içine gömülü komisyon
  const embedded = parseGarantiPosCommission(movement);
  if (embedded > 0) return { kind: 'pos', amount: embedded };

  if (!isOut || abs <= 0) return { kind: null, amount: 0 };

  // VakıfBank: ayrı giden POS komisyonu hareketi
  if (looksLikePosCommission(text)) return { kind: 'pos', amount: abs };

  // Havale / EFT masrafı, BSMV, damga, hesap işletim vb.
  if (BANK_FEE_TERMS.some((term) => text.includes(term))) {
    return { kind: 'transfer', amount: abs };
  }

  return { kind: null, amount: 0 };
};

/**
 * Verilen hareketlerin masraf/komisyon toplamı.
 * Gün süzmesi çağıran tarafta yapılır.
 */
export const summarizeBankFees = (movements) => {
  let transfer = 0;
  let pos = 0;
  let transferCount = 0;
  let posCount = 0;

  (movements || []).forEach((m) => {
    const { kind, amount } = classifyBankFee(m);
    if (kind === 'pos') {
      pos += amount;
      posCount += 1;
    } else if (kind === 'transfer') {
      transfer += amount;
      transferCount += 1;
    }
  });

  const round2 = (n) => Math.round(n * 100) / 100;
  return {
    transfer: round2(transfer),
    pos: round2(pos),
    total: round2(transfer + pos),
    transferCount,
    posCount
  };
};


/* ------------------------------------------------------------------ *
 * Pazaryeri hakediş ödemeleri
 *
 * Trendyol / Hepsiburada gibi platformlar satış hakedişini toplu EFT ile
 * yatırıyor. Bunlar mağazada kesilecek bir müşteri satışı değil; platformun
 * kendi ödemesi. Fatura adayı listesine girmemeli.
 *
 * Gerçek örnek (canlı veri):
 *   "131898 - 77015813 - TRENDYOL Marketplace Odeme/QNB BANK A.Ş.-0111-90001-434592
 *    sorgu numaralı DSM GRUP DANIŞMANLIK İLETİŞİM VE SATIŞ TİCARET A.Ş. tarafından
 *    MEHMET DEMIRSOY tarafına gelen EFT"
 * ------------------------------------------------------------------ */

const MARKETPLACE_TERMS = [
  'trendyol',
  'dsm grup',            // Trendyol'un tüzel kişiliği
  'marketplace odeme',
  'hepsiburada',
  'd market',            // Hepsiburada'nın tüzel kişiliği
  'dogus planet',        // n11
  'ciceksepeti',
  'pazarama',
  'amazon turkiye'
];

/** Metin bir pazaryeri hakediş ödemesini anlatıyor mu. */
export const looksLikeMarketplacePayoutText = (value) => {
  const text = fold(value);
  if (!text) return false;
  return MARKETPLACE_TERMS.some((term) => text.includes(term));
};

/** Hareket bir pazaryeri hakediş ödemesi mi (Trendyol vb.). */
export const isMarketplacePayoutMovement = (movement) => {
  if (!movement) return false;
  return [
    movement.counterpartyName,
    movement.senderName,
    movement.gonderenAdi,
    movement.description,
    movement.aciklama,
    movement.rawData?.senderName,
    movement.rawData?.gonderenAdi
  ].some(looksLikeMarketplacePayoutText);
};


/* ------------------------------------------------------------------ *
 * POS günsonu tahsilatları
 *
 * Kredi kartından çekilen paranın bankaya yatan karşılığı. Mağazada ayrıca
 * fatura kesilmiyor (satış zaten kasada işlenmiş), bu yüzden fatura adayı değil.
 *
 * Garanti : "PK3761132 BONUS 09/12 K: 220,66"            (net + gömülü komisyon)
 * VakıfBank: "... nolu pos ve batchinin satış işlemleri"  (brüt, komisyon ayrı)
 * ------------------------------------------------------------------ */

/** Gelen hareket bir POS günsonu tahsilatı mı. */
export const isPosSettlementMovement = (movement) => {
  if (!movement) return false;

  // Garanti: açıklamada "PK<ref> BONUS <ay/gün> K: <komisyon>"
  if (parseGarantiPosCommission(movement) > 0) return true;

  const text = feeHaystack(movement);
  // VakıfBank: işyeri/batch satış işlemleri
  if (/\bpos\b|batchin|batch/.test(text) && /satis islemleri|isyerinin/.test(text)) return true;
  // Garanti'nin komisyonsuz günsonu satırı
  if (/\bpk\d/.test(text) && /bonus/.test(text)) return true;

  return false;
};


/* ------------------------------------------------------------------ *
 * Nakit para yatırma (ATM / gişe)
 *
 * Mağazanın kasadaki nakdi bankaya yatırması; müşteri ödemesi değildir, fatura
 * adayı olamaz. Canlı veride (Kavaklı, Sefaköy) hiçbiri faturayla eşleşmemiş.
 *
 * VakıfBank: "… ATM 'sinde 8/12/2026 … tarihinde nakit para yatırıldı.İşlem no : … Cep Telefonu: …"
 * Garanti  : "ATM PARA YATIRMA-5170********9009-ATM Kodu:…", "QR ILE PARA YATIRMA-…", "PARA YATIRMA"
 * ------------------------------------------------------------------ */

/** Gelen hareket ATM'den / gişeden nakit para yatırma mı. */
export const isCashDepositMovement = (movement) => {
  if (!movement) return false;
  const description = fold(movement.description || movement.aciklama);
  if (!description) return false;
  if (/nakit para yatirildi/.test(description)) return true;
  if (/^(atm|qr ile|kartsiz) para yatirma\b/.test(description)) return true;
  // Garanti gişe yatırması: açıklama yalnızca "PARA YATIRMA"
  return description === 'para yatirma';
};

